// Kursdifferanse på valutabilag.
//
// Bilaget ble bokført med Norges Banks dagskurs — et estimat. Når betalingen
// dukker opp i banken, er den faktiske NOK-belastningen fasit: den inneholder
// kortutsteders påslag og den kursen som faktisk ble brukt.
//
// Hva som skal skje med differansen avhenger av om perioden er låst:
//
//   Åpen periode  -> korriger bilaget. Beløpet er aldri rapportert, så det
//                    finnes ingenting å korrigere mot. En kursdifferansepost
//                    ville bare vært støy.
//   Låst periode  -> behold bokført beløp og før differansen på 8060/8160.
//                    MVA-terminen er levert eller året er lukket, og det
//                    bokførte beløpet må stå.

type Voucher = {
  currency?: unknown
  totalGrossNok?: unknown
  totalGross?: unknown
}

type Locks = {
  vatFiled?: boolean
  yearClosed?: boolean
}

type ResolveArgs = {
  voucher?: Voucher
  actualNok?: unknown
  locks?: Locks
}

type ResolveResult =
  | { action: 'none'; reason: 'not-foreign-currency' | 'missing-amount' | 'no-difference' }
  | {
      action: 'post-difference'
      accountCode: string
      amount: number
      difference: number
      bookedNok: number
      actualNok: number
      reason: 'year-closed' | 'vat-filed'
    }
  | {
      action: 'adjust'
      amountNok: number
      difference: number
      bookedNok: number
      actualNok: number
      exchangeRate: number | undefined
      exchangeRateSource: 'bank-actual'
      reason: 'period-open'
    }

;(function initForeignCurrencyDifference(global: typeof globalThis) {
  // Kontoene finnes allerede i kontoplanen (src/tax-income-balance-accounts.jsx).
  const GAIN_ACCOUNT = '8060' // Gevinst ved kursendring på valuta
  const LOSS_ACCOUNT = '8160' // Tap ved kursendring på valuta
  const ROUNDING_LIMIT = 0.005

  const round = (value: number): number => Math.round(value * 100) / 100

  const resolve = ({ voucher, actualNok, locks }: ResolveArgs = {}): ResolveResult => {
    const currency = String((voucher && voucher.currency) || 'NOK').toUpperCase()
    const bookedNok = Number(voucher && voucher.totalGrossNok)
    const actual = Math.abs(Number(actualNok))

    // Et NOK-bilag har ingen kursdifferanse. Et avvik der er en annen feil, og
    // skal ikke skjules som agio.
    if (currency === 'NOK') return { action: 'none', reason: 'not-foreign-currency' }
    if (!(bookedNok > 0) || !(actual > 0)) return { action: 'none', reason: 'missing-amount' }

    const difference = round(actual - bookedNok)
    if (Math.abs(difference) < ROUNDING_LIMIT) return { action: 'none', reason: 'no-difference' }

    if (locks && (locks.vatFiled || locks.yearClosed)) {
      return {
        action: 'post-difference',
        // Hoyere faktisk kostnad enn bokfort er et tap paa valuta, lavere er en
        // gevinst. Fortegnet folger kjopet, ikke banklinjen.
        accountCode: difference > 0 ? LOSS_ACCOUNT : GAIN_ACCOUNT,
        amount: Math.abs(difference),
        difference,
        bookedNok,
        actualNok: actual,
        reason: locks.yearClosed ? 'year-closed' : 'vat-filed',
      }
    }

    return {
      action: 'adjust',
      amountNok: actual,
      difference,
      bookedNok,
      actualNok: actual,
      // Kursen erstattes av den faktiske: bilaget skal vise hvilken kurs som
      // ble brukt, ikke estimatet som ble forkastet.
      exchangeRate: Number(voucher?.totalGross) > 0 ? round(actual / Number(voucher?.totalGross)) : undefined,
      exchangeRateSource: 'bank-actual',
      reason: 'period-open',
    }
  }

  ;(global as any).PlummeForeignCurrencyDifference = { resolve, GAIN_ACCOUNT, LOSS_ACCOUNT }
})(typeof window !== 'undefined' ? window : globalThis)
